<template>
  <n-config-provider v-bind="langConfig" :theme-overrides="theme" abstract>
    <NuxtLoadingIndicator :height="4" :color="colors.red['50']" />
    <NuxtLayout>
      <NuxtPage />
      <client-only>
        <full-page-loading />
        <global-alert />
        <global-content-viewer />
        <lang-switcher />
        <cs-bubble v-if="isCsBubbleShown" :has-unread-messages="hasUnreadMessages" @click="handleOpen" />
      </client-only>
    </NuxtLayout>
  </n-config-provider>
</template>

<script setup lang="ts">
import { NConfigProvider } from 'naive-ui'

import { colors } from '@/theme.config'
import theme from '@/naive-ui-theme'

import { GtmEvent } from '@/types/gtm'
import { useAppStore } from '@/store/app'

const GlobalAlert = defineAsyncComponent(() => dynamicImport(() => import('@/components/common/global-alert.vue'), 'global-alert'))
const FullPageLoading = defineAsyncComponent(() => dynamicImport(() => import('@/components/common/full-page-loading.vue'), 'full-page-loading'))
const GlobalContentViewer = defineAsyncComponent(() =>
  dynamicImport(() => import('@/components/common/global-content-viewer.vue'), 'global-content-viewer')
)
const LangSwitcher = defineAsyncComponent(() => dynamicImport(() => import('@/components/common/lang-switcher.vue'), 'lang-switcher'))
const CsBubble = defineAsyncComponent(() => dynamicImport(() => import('@/components/common/cs-bubble.vue'), 'cs-bubble'))

const nuxtApp = useNuxtApp()
const { locale } = useI18n()
const { nUILocale, nUIDateLocale } = useNUILocale()

const { regionId } = useRegion()
const appStore = useAppStore()
const { geolocation, isFullPageLoadingShown } = storeToRefs(appStore)

const { isCsBubbleShown, hasUnreadMessages, initialCs, handleOpen } = useCustomerService()
const { isLocaleMessageLoading } = useLocaleMessages()

useRegionIdHandler()
useEtgSeoSetting()
useSentryRegister()
useAttribution(nuxtApp)

const langConfig = computed(() => ({ locale: nUILocale.value, dateLocale: nUIDateLocale.value }))

onMounted(() => {
  nuxtApp.$trackEvent(GtmEvent.SET_USER_PROFILE)

  initialCs()
})

watch([locale, regionId, geolocation], () => {
  nuxtApp.$trackEvent(GtmEvent.SET_USER_PROFILE)
})

watch(isLocaleMessageLoading, newIsLocaleMessageLoading => {
  isFullPageLoadingShown.value = newIsLocaleMessageLoading
})
</script>

<style lang="scss" src="@/assets/styles/naive-ui-overrides.scss"></style>
